import React from 'react';
import { motion } from 'framer-motion';
import {
  Users, BookOpen, Eye, CreditCard, TrendingUp,
  Crown, Clock, Trash2, Shield, ShieldOff, BarChart3,
  Link2, Percent, Euro, Plus, Copy, Check as CheckIcon, X,
  Ban, UserCheck, Gift, CalendarPlus, Download, History, MoreVertical, Mail, Send, Tag
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { TabsContent } from '../../ui/tabs';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';

export const AffiliatesTab = ({ ctx }) => {
  const { affiliates, setShowAffiliateModal, copyCode, copiedCode, handleToggleAffiliate, handleDeleteAffiliate } = ctx;
  return (
  <TabsContent value="affiliates" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Influencer / Affiliate Partners</h2>
                <Button
                  data-testid="create-affiliate-btn"
                  onClick={() => setShowAffiliateModal(true)}
                  className="bg-gold hover:bg-gold/90 text-night-blue"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe Affiliate
                </Button>
              </div>

              {affiliates.length === 0 ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-12 text-center">
                    <Link2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">Nog geen affiliate partners</p>
                    <Button
                      onClick={() => setShowAffiliateModal(true)}
                      variant="outline"
                    >
                      Eerste affiliate toevoegen
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {affiliates.map(affiliate => (
                    <Card key={affiliate.id} className={`border-0 shadow-sm ${!affiliate.is_active ? 'opacity-60' : ''}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{affiliate.name}</h3>
                              {!affiliate.is_active && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                  Inactief
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 mb-3">
                              <code className="bg-muted px-3 py-1.5 rounded-lg font-mono text-sm font-bold">
                                {affiliate.code}
                              </code>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyCode(affiliate.code)}
                                className="h-8 w-8 p-0"
                              >
                                {copiedCode === affiliate.code ? (
                                  <CheckIcon className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                            </div>

                            {affiliate.email && (
                              <p className="text-sm text-muted-foreground mb-2">{affiliate.email}</p>
                            )}

                            <div className="flex flex-wrap gap-4 text-sm">
                              <div className="flex items-center gap-1.5">
                                <Percent className="w-4 h-4 text-gold" />
                                <span>{affiliate.discount_percentage}% korting</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Euro className="w-4 h-4 text-green-600" />
                                <span>{affiliate.commission_percentage}% commissie</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-3">
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <p className="text-2xl font-bold">{affiliate.total_signups}</p>
                                <p className="text-xs text-muted-foreground">Aanmeldingen</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-green-600">€{affiliate.total_revenue?.toFixed(2) || '0.00'}</p>
                                <p className="text-xs text-muted-foreground">Omzet</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-gold">€{affiliate.pending_payout?.toFixed(2) || '0.00'}</p>
                                <p className="text-xs text-muted-foreground">Uit te betalen</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleToggleAffiliate(affiliate.id, affiliate.is_active)}
                              >
                                {affiliate.is_active ? 'Deactiveren' : 'Activeren'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteAffiliate(affiliate.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
  );
};
